import { useCallback, useState } from 'react';
import { useIntl } from 'react-intl';
import { useRouter } from 'next/navigation';

import { revalidate } from '@/app/actions';

import useLocalization from '@/hooks/useLocalization';

import { ReservationOption } from '@/models';
import { GuestPortalService } from '@/services/GuestPortal';
import { ClientAuthService } from '@/services/client';
import { ClientCookiesStore } from '@/services/cookies/client';
import { LOGIN_STEP, LoginState, UseLoginReturn } from './types';

export function useLogin({
  propertyId,
  redirect,
}: {
  propertyId: string;
  redirect?: string;
}): UseLoginReturn {
  const [state, setState] = useState<LoginState>({
    reservationId: '',
    lastName: '',
    firstName: '',
    arrivalDate: '',
    reservationNotFound: false,
    guestNotFound: false,
    loading: false,
    errorText: '',
    submittedFields: [],
    currentStep: LOGIN_STEP.RESERVATIONID,
    matchingReservations: [],
  });
  const intl = useIntl();
  const router = useRouter();
  const { locale } = useLocalization();

  const {
    reservationId,
    lastName,
    firstName,
    reservationNotFound,
    loading,
    submittedFields,
    matchingReservations,
  } = state;

  const setStateField = useCallback(
    <K extends keyof LoginState>(field: K, value: LoginState[K]) => {
      setState((state) => ({
        ...state,
        [field]: value,
      }));
    },
    [setState],
  );

  const onSubmit = useCallback(
    async (e: React.FormEvent<HTMLFormElement>) => {
      e.preventDefault();

      const submittedFields = [
        reservationId && 'reservationId',
        lastName && 'lastName',
        firstName && 'firstName',
      ].filter(Boolean) as string[];

      setStateField('loading', true);

      try {
        const response = await GuestPortalService.identifyGuest({
          propertyId,
          reservationId,
          lastName: lastName || undefined,
          firstName: firstName || undefined,
        });

        if ('type' in response && response.type === 'reservation-not-found') {
          return setState((state) => ({
            ...state,
            reservationNotFound: true,
            guestNotFound: false,
            loading: false,
            submittedFields,
          }));
        }

        if ('type' in response && response.type === 'guest-not-found') {
          return setState((state) => ({
            ...state,
            reservationNotFound: false,
            guestNotFound: true,
            loading: false,
            submittedFields: !firstName
              ? submittedFields.filter((field) => field !== 'lastName')
              : submittedFields,
          }));
        }

        if ('type' in response && response.type === 'multiple-guests-found') {
          return setState((state) => ({
            ...state,
            reservationNotFound: false,
            guestNotFound: false,
            loading: false,
            submittedFields,
            matchingReservations: response.reservations,
          }));
        }

        if ('guestId' in response) {
          const authData = await GuestPortalService.generateGuestJWT({
            propertyId,
            token: response.token,
          });

          await ClientAuthService.setGuestPortalAuthData(authData);

          if (redirect) {
            await revalidate(redirect);
            router.push(decodeURIComponent(redirect));
            return;
          }

          const guestBookSlug =
            ClientCookiesStore.getGuestBookSlug() || 'guest';
          const redirectPath = reservationId
            ? `/${locale}/${propertyId}/${guestBookSlug}?reservationId=${reservationId}`
            : `/${locale}/${propertyId}/${guestBookSlug}`;

          await revalidate(redirectPath);

          router.push(redirectPath);
        }
      } catch (e) {
        console.error(e);

        setState((state) => ({
          ...state,
          loading: false,
          submittedFields,
          errorText: intl.formatMessage({
            id: 'something_went_wrong',
            defaultMessage: 'Oops! Something went wrong',
          }),
        }));
      }
    },
    [
      firstName,
      intl,
      lastName,
      locale,
      propertyId,
      redirect,
      reservationId,
      router,
      setStateField,
    ],
  );

  const isReservationStep =
    !submittedFields.includes('reservationId') || reservationNotFound;
  const isLastNameStep =
    !isReservationStep && !submittedFields.includes('lastName');
  const isFirstNameStep =
    !isReservationStep &&
    !isLastNameStep &&
    !submittedFields.includes('firstName');
  const isSelectReservationStep =
    matchingReservations.length > 0 &&
    !reservationNotFound &&
    submittedFields.includes('reservationId') &&
    submittedFields.includes('lastName');

  const isDisabled =
    loading ||
    (isReservationStep && !reservationId) ||
    (isLastNameStep && !lastName) ||
    (isFirstNameStep && !firstName) ||
    isSelectReservationStep;

  const canGoBack = submittedFields.length > 0 && !isReservationStep;
  const currentStep = isSelectReservationStep
    ? LOGIN_STEP.SELECT_RESERVATION
    : isReservationStep
      ? LOGIN_STEP.RESERVATIONID
      : isLastNameStep
        ? LOGIN_STEP.LASTNAME
        : isFirstNameStep
          ? LOGIN_STEP.FIRSTNAME
          : LOGIN_STEP.RESERVATIONID;

  const onBack = useCallback(() => {
    setState((state) => ({
      ...state,
      submittedFields: state.submittedFields.slice(0, -1),
      reservationNotFound: false,
      guestNotFound: false,
      lastName: isLastNameStep ? '' : state.lastName,
      firstName: firstName ? '' : state.firstName,
    }));
  }, [firstName, isLastNameStep]);

  const onSelectReservation = useCallback(
    async (selectedReservation: ReservationOption) => {
      setState((s) => ({ ...s, loading: true }));

      try {
        const response = await GuestPortalService.identifyGuest({
          propertyId,
          reservationId: selectedReservation.reservationId,
          lastName: lastName || undefined,
          firstName: firstName || undefined,
        });

        if ('guestId' in response) {
          const authData = await GuestPortalService.generateGuestJWT({
            propertyId,
            token: response.token,
          });

          await ClientAuthService.setGuestPortalAuthData(authData);

          const guestBookSlug =
            ClientCookiesStore.getGuestBookSlug() || 'guest';
          const redirectPath = `/${locale}/${propertyId}/${guestBookSlug}?reservationId=${selectedReservation.reservationId}`;

          await revalidate(redirectPath);

          router.push(redirect ? decodeURIComponent(redirect) : redirectPath);
        }
      } catch (e) {
        console.error(e);

        setState((state) => ({
          ...state,
          loading: false,
          errorText: intl.formatMessage({
            id: 'something_went_wrong',
            defaultMessage: 'Oops! Something went wrong',
          }),
        }));
      }
    },
    [intl, propertyId, lastName, firstName, locale, redirect, router],
  );

  return {
    state,
    setState,
    setStateField,
    isSubmitDisabled: isDisabled,
    onSubmit,
    onBack,
    canGoBack,
    currentStep,
    onSelectReservation,
  };
}
